import { CloudOff, type LucideIcon } from 'lucide-react';
import type { CSSProperties, ReactNode } from 'react';
import { toAppError } from '../../../api/errors';
import { useT } from '../../../l10n/i18n';
import { errorMessage, toneColors, type Tone } from '../presentation';

/** A small rounded label: item type, status, revision, workspace. */
export function Pill({
  label,
  tone,
  color,
  background,
  icon: Icon,
  trailing: Trailing,
  dot,
  pulse = false,
  dense = false,
}: {
  label: string;
  tone?: Tone;
  color?: string;
  background?: string;
  icon?: LucideIcon;
  /** After the label, for a pill that opens something (a chevron). */
  trailing?: LucideIcon;
  /** A colored dot before the label (workspaces, work going on). */
  dot?: string;
  /** The dot breathes, for work going on right now. */
  pulse?: boolean;
  dense?: boolean;
}) {
  const c = tone ? toneColors(tone) : undefined;
  const style: CSSProperties = { color: color ?? c?.fg, background: background ?? c?.bg };
  const size = dense ? 12 : 13;
  return (
    <span className={dense ? 'pill dense' : 'pill'} style={style}>
      {dot && <span className={pulse ? 'dot pulse' : 'dot'} style={{ background: dot }} />}
      {Icon && <Icon size={size} strokeWidth={2.4} aria-hidden />}
      <span className="ellipsis">{label}</span>
      {Trailing && <Trailing size={size + 2} strokeWidth={2.4} aria-hidden />}
    </span>
  );
}

export function Skeleton({ width, height = 16, radius }: { width?: number | string; height?: number; radius?: number }) {
  return <div className="skeleton" style={{ width: width ?? '100%', height, borderRadius: radius }} aria-hidden />;
}

/** A calm, centered message with an illustration. */
export function EmptyState({ title, body, art, action }: { title: string; body?: string; art?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      {art && <div className="art">{art}</div>}
      <h2 className="headline-small">{title}</h2>
      {body && <p className="body-medium muted">{body}</p>}
      {action && <div className="action">{action}</div>}
    </div>
  );
}

/** A failed load with a way to try again. */
export function ErrorView({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const t = useT();
  return (
    <EmptyState
      art={
        <div style={{ display: 'grid', placeItems: 'center', width: 64, height: 64, borderRadius: 22, background: 'var(--danger-soft)' }}>
          <CloudOff size={30} color="var(--danger)" aria-hidden />
        </div>
      }
      title={errorMessage(t, toAppError(error))}
      action={
        <button type="button" className="btn btn-tonal" onClick={onRetry}>
          {t('actionRetry')}
        </button>
      }
    />
  );
}

/** A titled block of a page. */
export function Section({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) {
  return (
    <section className="section">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 12 }}>
        <h2 className="overline" style={{ margin: 0 }}>
          {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}
